/**
 * Single source of truth for the "How GEO Observatory works" explainer sections.
 *
 * Both the page (which renders the section blocks) and `SectionNav` (the
 * sticky scroll-spy nav) consume this registry, so the nav can never drift
 * from the sections that actually exist on the page. The `id` doubles as the
 * scroll anchor target and the suffix of each section's `data-testid`
 * (`hiw-section-<id>`).
 */
export interface HowItWorksSection {
  /** Anchor id + testid suffix. Stable — referenced by behavioral tests. */
  readonly id: 'what' | 'llm-view' | 'fan-out' | 'collect' | 'personas' | 'geo';
  /** Short nav label / section heading. */
  readonly title: string;
  /** One-line nav subtitle (eyebrow), kept terse. */
  readonly tagline: string;
}

export const HOW_IT_WORKS_SECTIONS = [
  { id: 'what', title: 'What GEO Observatory does', tagline: 'The monitoring loop' },
  { id: 'llm-view', title: 'How LLMs see your site', tagline: 'Bots read structure, not pixels' },
  { id: 'fan-out', title: 'Prompts vs. the engine fan-out', tagline: 'One question, many sub-queries' },
  { id: 'collect', title: 'How GEO Observatory collects data', tagline: 'Five engines, sources, mentions' },
  { id: 'personas', title: 'Personas & their role', tagline: 'Scoring through a lens' },
  { id: 'geo', title: 'GEO, in one minute', tagline: 'Being the answer, not the link' },
] as const satisfies readonly HowItWorksSection[];

export type HowItWorksSectionId = (typeof HOW_IT_WORKS_SECTIONS)[number]['id'];
